import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import api from '../utils/api';
import toast from 'react-hot-toast';

const STATES = ['Andhra Pradesh','Telangana','Tamil Nadu','Karnataka','Kerala','Maharashtra','Delhi','Gujarat','Rajasthan','West Bengal','Bihar','Odisha','Madhya Pradesh','Uttar Pradesh','Other'];

const NAV = [
  { key:'profile',   icon:'👤', label:'My Profile'       },
  { key:'addresses', icon:'📍', label:'Saved Addresses'  },
  { key:'referral',  icon:'🎁', label:'Refer & Earn'     },
  { key:'settings',  icon:'⚙️', label:'Settings'         },
];

// ── Setting toggle — standalone component (hooks allowed here) ──
function SettingToggle({ setting }) {
  const key = `vkg_setting_${setting.label.replace(/\s+/g,'_')}`;
  const [on, setOn] = React.useState(() => {
    try { const v = localStorage.getItem(key); return v === null ? setting.defaultOn : v === 'true'; }
    catch { return setting.defaultOn; }
  });
  return (
    <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',padding:'16px 0',borderBottom:'1px solid #f0e8e0'}}>
      <div>
        <div style={{fontFamily:"'Jost',sans-serif",fontWeight:600,fontSize:14,color:'#2d1f1f',marginBottom:2}}>{setting.label}</div>
        <div style={{fontSize:12,color:'#8a7968'}}>{setting.desc}</div>
      </div>
      <button onClick={() => { const v = !on; setOn(v); localStorage.setItem(key, v); toast.success(v ? 'Enabled' : 'Disabled'); }}
        style={{width:44,height:24,borderRadius:12,background:on?'#7a1f1f':'#e8ddd0',border:'none',cursor:'pointer',transition:'background 0.3s',position:'relative',flexShrink:0}}>
        <div style={{width:18,height:18,borderRadius:'50%',background:'#fff',position:'absolute',top:3,left:on?23:3,transition:'left 0.3s',boxShadow:'0 1px 4px rgba(0,0,0,0.2)'}}/>
      </button>
    </div>
  );
}

export default function AccountPage() {
  const { user, logout } = useAuth();
  const navigate          = useNavigate();
  const [tab,      setTab]      = useState('profile');
  const [form,     setForm]     = useState({ first_name:'', last_name:'', phone:'' });
  const [saving,   setSaving]   = useState(false);
  const [addresses, setAddresses] = useState([]);
  const [showAddAddr, setShowAddAddr] = useState(false);
  const [newAddr,  setNewAddr]  = useState({ label:'Home', name:'', phone:'', address:'', city:'', state:'Andhra Pradesh', pincode:'' });
  const [referCode, setReferCode] = useState('');
  const [copied,   setCopied]   = useState(false);

  useEffect(() => {
    if (user) {
      setForm({ first_name: user.first_name||'', last_name: user.last_name||'', phone: user.phone||'' });
      // Generate referral code from user id
      setReferCode(`VKG${String(user.id).slice(-4).toUpperCase()}${user.first_name.slice(0,3).toUpperCase()}`);
      // Load saved addresses from localStorage (can be moved to DB)
      try { const saved = JSON.parse(localStorage.getItem(`vkg_addresses_${user.id}`) || '[]'); setAddresses(saved); } catch {}
    }
  }, [user]);

  if (!user) return (
    <div className="page" style={{textAlign:'center',paddingTop:120}}>
      <div style={{fontSize:56}}>👤</div>
      <h2 style={{fontFamily:"'Playfair Display',serif",color:'#7a1f1f',fontStyle:'italic',margin:'12px 0 16px'}}>My Account</h2>
      <Link to="/login" className="btn btn-primary">Login</Link>
    </div>
  );

  const handleSaveProfile = async e => {
    e.preventDefault();
    setSaving(true);
    try { await api.put('/auth/profile', form); toast.success('Profile updated! ✓'); }
    catch (err) { toast.error(err.message); }
    finally { setSaving(false); }
  };

  const saveAddress = () => {
    if (!newAddr.name||!newAddr.phone||!newAddr.address||!newAddr.city||!newAddr.pincode) { toast.error('Fill all address fields'); return; }
    const updated = [...addresses, { ...newAddr, id: Date.now() }];
    setAddresses(updated);
    localStorage.setItem(`vkg_addresses_${user.id}`, JSON.stringify(updated));
    setShowAddAddr(false);
    setNewAddr({ label:'Home', name:'', phone:'', address:'', city:'', state:'Andhra Pradesh', pincode:'' });
    toast.success('Address saved!');
  };

  const deleteAddress = id => {
    const updated = addresses.filter(a => a.id !== id);
    setAddresses(updated);
    localStorage.setItem(`vkg_addresses_${user.id}`, JSON.stringify(updated));
    toast.success('Address removed');
  };

  const copyReferCode = () => {
    navigator.clipboard.writeText(referCode).then(()=>{ setCopied(true); setTimeout(()=>setCopied(false),2000); });
    toast.success('Referral code copied!');
  };

  const shareReferral = () => {
    const text = `Shop handwoven sarees at Venkatagiri Handlooms! Use my code ${referCode} to get ₹200 off your first order 🥻\nhttps://venkatagirihandlooms.com`;
    if (navigator.share) navigator.share({ title:'Venkatagiri Handlooms', text });
    else { navigator.clipboard.writeText(text); toast.success('Share text copied!'); }
  };

  const inp = { width:'100%', padding:'11px 13px', border:'1.5px solid var(--border)', background:'#fff', fontSize:14, outline:'none', borderRadius:0, WebkitAppearance:'none', fontFamily:'inherit' };

  return (
    <div className="page">
      <div className="container" style={{padding:'48px 24px',maxWidth:960}}>
        <h1 style={{fontFamily:"'Playfair Display',serif",fontSize:32,color:'#7a1f1f',fontStyle:'italic',marginBottom:32}}>My Account</h1>

        <div style={{display:'grid',gridTemplateColumns:'220px 1fr',gap:40,alignItems:'start'}}>

          {/* ── SIDEBAR ── */}
          <div>
            {/* Avatar */}
            <div style={{textAlign:'center',padding:'20px',background:'linear-gradient(135deg,#7a1f1f,#5a1515)',marginBottom:16}}>
              <div style={{width:64,height:64,borderRadius:'50%',background:'rgba(255,255,255,0.15)',border:'2px solid rgba(201,168,76,0.5)',color:'#e8c96a',display:'flex',alignItems:'center',justifyContent:'center',fontFamily:"'Playfair Display',serif",fontSize:28,margin:'0 auto 10px'}}>
                {user.first_name[0].toUpperCase()}
              </div>
              <div style={{fontFamily:"'Cormorant Garamond',serif",fontSize:18,color:'#fff',fontWeight:600}}>{user.first_name} {user.last_name}</div>
              <div style={{fontSize:11,color:'rgba(255,255,255,0.6)',marginTop:2,fontFamily:"'Jost',sans-serif"}}>{user.email}</div>
            </div>

            {/* Nav */}
            {NAV.map(n => (
              <button key={n.key} onClick={()=>setTab(n.key)} style={{display:'flex',alignItems:'center',gap:10,width:'100%',padding:'12px 16px',background:tab===n.key?'#fdf3f0':'transparent',border:'none',borderLeft:tab===n.key?'3px solid #7a1f1f':'3px solid transparent',cursor:'pointer',fontFamily:"'Jost',sans-serif",fontSize:13,color:tab===n.key?'#7a1f1f':'#2d1f1f',textAlign:'left',transition:'all 0.2s'}}>
                <span>{n.icon}</span> {n.label}
              </button>
            ))}
            <div style={{margin:'8px 0',borderTop:'1px solid var(--border)'}}/>
            {[{to:'/orders',label:'📦 My Orders'},{to:'/wishlist',label:'❤️ Wishlist'}].map(l=>(
              <Link key={l.to} to={l.to} style={{display:'flex',alignItems:'center',gap:10,padding:'12px 16px',fontSize:13,color:'#2d1f1f',textDecoration:'none',fontFamily:"'Jost',sans-serif"}}>
                {l.label}
              </Link>
            ))}
            <button onClick={()=>{logout();navigate('/');}} style={{display:'flex',alignItems:'center',gap:10,width:'100%',padding:'12px 16px',background:'none',border:'none',cursor:'pointer',fontFamily:"'Jost',sans-serif",fontSize:13,color:'#c00',textAlign:'left'}}>
              🚪 Logout
            </button>
          </div>

          {/* ── CONTENT PANELS ── */}
          <div>

            {/* ── PROFILE ── */}
            {tab==='profile' && (
              <div style={{background:'#fff',border:'1px solid var(--border)',padding:28}}>
                <h2 style={{fontFamily:"'Cormorant Garamond',serif",fontSize:22,color:'#2d1f1f',marginBottom:24}}>Edit Profile</h2>
                <form onSubmit={handleSaveProfile}>
                  <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:14}}>
                    <div className="form-group"><label className="form-label">First Name</label><input style={inp} value={form.first_name} onChange={e=>setForm(f=>({...f,first_name:e.target.value}))}/></div>
                    <div className="form-group"><label className="form-label">Last Name</label><input style={inp} value={form.last_name} onChange={e=>setForm(f=>({...f,last_name:e.target.value}))}/></div>
                  </div>
                  <div className="form-group"><label className="form-label">Phone</label><input style={inp} value={form.phone} onChange={e=>setForm(f=>({...f,phone:e.target.value}))} placeholder="+91 XXXXX XXXXX"/></div>
                  <div className="form-group"><label className="form-label">Email (cannot change)</label><input style={{...inp,background:'#f7f0e6',color:'#aaa'}} value={user.email} disabled/></div>
                  <button type="submit" className="btn btn-primary" disabled={saving}>{saving?'Saving...':'Save Changes'}</button>
                </form>
              </div>
            )}

            {/* ── SAVED ADDRESSES ── */}
            {tab==='addresses' && (
              <div>
                <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:20}}>
                  <h2 style={{fontFamily:"'Cormorant Garamond',serif",fontSize:22,color:'#2d1f1f'}}>Saved Addresses</h2>
                  <button onClick={()=>setShowAddAddr(true)} className="btn btn-outline" style={{fontSize:11,padding:'8px 16px'}}>+ Add New</button>
                </div>

                {addresses.length===0 && !showAddAddr && (
                  <div style={{textAlign:'center',padding:'48px 0',background:'#fff',border:'1px solid var(--border)'}}>
                    <div style={{fontSize:48}}>📍</div>
                    <p style={{color:'#8a7968',marginTop:12,marginBottom:16,fontFamily:"'Cormorant Garamond',serif",fontSize:17}}>No saved addresses yet</p>
                    <button onClick={()=>setShowAddAddr(true)} className="btn btn-primary" style={{fontSize:11}}>Add Your First Address</button>
                  </div>
                )}

                {addresses.map(addr=>(
                  <div key={addr.id} style={{background:'#fff',border:'1px solid var(--border)',padding:20,marginBottom:12,position:'relative'}}>
                    <div style={{display:'flex',justifyContent:'space-between',alignItems:'flex-start'}}>
                      <div>
                        <div style={{display:'inline-block',background:'#f0e8e0',padding:'2px 10px',fontSize:10,fontWeight:700,letterSpacing:1,textTransform:'uppercase',color:'#7a1f1f',fontFamily:"'Jost',sans-serif",marginBottom:8}}>{addr.label}</div>
                        <div style={{fontFamily:"'Cormorant Garamond',serif",fontSize:17,color:'#1a0f0f',fontWeight:600,marginBottom:4}}>{addr.name} — {addr.phone}</div>
                        <div style={{fontSize:13,color:'#5a3a3a',lineHeight:1.7}}>{addr.address}, {addr.city}, {addr.state} — {addr.pincode}</div>
                      </div>
                      <button onClick={()=>deleteAddress(addr.id)} style={{fontSize:12,color:'#c00',background:'none',border:'none',cursor:'pointer',fontFamily:"'Jost',sans-serif",textDecoration:'underline',flexShrink:0}}>Remove</button>
                    </div>
                  </div>
                ))}

                {showAddAddr && (
                  <div style={{background:'#fff',border:'1px solid var(--border)',padding:24,marginTop:16}}>
                    <h3 style={{fontFamily:"'Cormorant Garamond',serif",fontSize:20,color:'#2d1f1f',marginBottom:20}}>Add New Address</h3>
                    <div className="form-group">
                      <label className="form-label">Label</label>
                      <div style={{display:'flex',gap:8}}>
                        {['Home','Work','Other'].map(l=><button key={l} onClick={()=>setNewAddr(a=>({...a,label:l}))} style={{padding:'8px 20px',border:'1.5px solid',cursor:'pointer',fontFamily:"'Jost',sans-serif",fontSize:12,borderColor:newAddr.label===l?'#7a1f1f':'#e8ddd0',background:newAddr.label===l?'#7a1f1f':'transparent',color:newAddr.label===l?'#fff':'#5a3a3a'}}>{l}</button>)}
                      </div>
                    </div>
                    <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:12}}>
                      <div className="form-group"><label className="form-label">Full Name *</label><input style={inp} value={newAddr.name} onChange={e=>setNewAddr(a=>({...a,name:e.target.value}))} placeholder="Full name"/></div>
                      <div className="form-group"><label className="form-label">Phone *</label><input style={inp} value={newAddr.phone} onChange={e=>setNewAddr(a=>({...a,phone:e.target.value}))} placeholder="10-digit mobile"/></div>
                    </div>
                    <div className="form-group"><label className="form-label">Address *</label><textarea style={{...inp,minHeight:72,resize:'vertical'}} value={newAddr.address} onChange={e=>setNewAddr(a=>({...a,address:e.target.value}))} placeholder="House/Flat no, Street, Area"/></div>
                    <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:12}}>
                      <div className="form-group"><label className="form-label">City *</label><input style={inp} value={newAddr.city} onChange={e=>setNewAddr(a=>({...a,city:e.target.value}))}/></div>
                      <div className="form-group"><label className="form-label">Pincode *</label><input style={inp} value={newAddr.pincode} onChange={e=>setNewAddr(a=>({...a,pincode:e.target.value.replace(/\D/g,'').slice(0,6)}))} placeholder="6 digits"/></div>
                    </div>
                    <div className="form-group">
                      <label className="form-label">State</label>
                      <select style={inp} value={newAddr.state} onChange={e=>setNewAddr(a=>({...a,state:e.target.value}))}>
                        {STATES.map(s=><option key={s}>{s}</option>)}
                      </select>
                    </div>
                    <div style={{display:'flex',gap:10,marginTop:4}}>
                      <button onClick={()=>setShowAddAddr(false)} className="btn btn-outline" style={{flex:1,justifyContent:'center',padding:12,fontSize:11}}>Cancel</button>
                      <button onClick={saveAddress} className="btn btn-primary" style={{flex:2,justifyContent:'center',padding:12,fontSize:11}}>Save Address</button>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* ── REFERRAL ── */}
            {tab==='referral' && (
              <div>
                {/* Hero */}
                <div style={{background:'linear-gradient(135deg,#7a1f1f,#5a1515)',padding:32,textAlign:'center',marginBottom:20,position:'relative',overflow:'hidden'}}>
                  <div style={{position:'absolute',inset:0,opacity:0.08,backgroundImage:`url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='32' height='32' viewBox='0 0 32 32'%3E%3Cg fill='none' stroke='%23c9a84c' stroke-width='0.4'%3E%3Cpolygon points='16,3 29,16 16,29 3,16'/%3E%3C/g%3E%3C/svg%3E")`,backgroundSize:'32px 32px'}}/>
                  <div style={{position:'relative',zIndex:2}}>
                    <div style={{fontSize:48,marginBottom:12}}>🎁</div>
                    <h2 style={{fontFamily:"'Playfair Display',serif",fontSize:24,color:'#fff',fontStyle:'italic',marginBottom:8}}>Refer & Earn</h2>
                    <p style={{fontFamily:"'Cormorant Garamond',serif",fontSize:17,color:'rgba(255,255,255,0.75)',fontStyle:'italic'}}>Share your love for handlooms and earn rewards!</p>
                  </div>
                </div>

                {/* How it works */}
                <div style={{background:'#fff',border:'1px solid var(--border)',padding:24,marginBottom:16}}>
                  <h3 style={{fontFamily:"'Cormorant Garamond',serif",fontSize:20,color:'#2d1f1f',marginBottom:16}}>How it Works</h3>
                  <div style={{display:'grid',gridTemplateColumns:'repeat(3,1fr)',gap:16}}>
                    {[
                      {step:'1',icon:'📤',title:'Share Your Code',desc:'Share your unique referral code with friends & family'},
                      {step:'2',icon:'🛒',title:'They Shop',desc:'Your friend gets ₹200 off their first order'},
                      {step:'3',icon:'💰',title:'You Earn',desc:'You get ₹300 credit when they place their order'},
                    ].map(s=>(
                      <div key={s.step} style={{textAlign:'center',padding:16,background:'#f7f0e6',border:'1px solid var(--border)'}}>
                        <div style={{fontSize:32,marginBottom:8}}>{s.icon}</div>
                        <div style={{fontFamily:"'Jost',sans-serif",fontWeight:700,fontSize:13,color:'#7a1f1f',marginBottom:4}}>{s.title}</div>
                        <div style={{fontSize:12,color:'#8a7968',lineHeight:1.5}}>{s.desc}</div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Code box */}
                <div style={{background:'#fff',border:'1px solid var(--border)',padding:24}}>
                  <h3 style={{fontFamily:"'Cormorant Garamond',serif",fontSize:20,color:'#2d1f1f',marginBottom:16}}>Your Referral Code</h3>
                  <div style={{display:'flex',gap:10,marginBottom:20,flexWrap:'wrap'}}>
                    <div style={{flex:1,padding:'14px 20px',background:'#f7f0e6',border:'2px dashed #c9a84c',fontFamily:"'Jost',sans-serif",fontSize:22,fontWeight:700,letterSpacing:4,color:'#7a1f1f',textAlign:'center',minWidth:180}}>
                      {referCode}
                    </div>
                    <button onClick={copyReferCode} className="btn btn-outline" style={{fontSize:11,padding:'14px 20px'}}>
                      {copied?'✓ Copied!':'📋 Copy'}
                    </button>
                  </div>
                  <button onClick={shareReferral} className="btn btn-primary" style={{width:'100%',justifyContent:'center',padding:14,fontSize:12}}>
                    📤 Share with Friends
                  </button>
                  <div style={{marginTop:16,padding:12,background:'#e8f5e9',fontSize:12,color:'#2e7d32',fontFamily:"'Jost',sans-serif",lineHeight:1.6}}>
                    🎉 Your friends save ₹200 · You earn ₹300 store credit per successful referral
                  </div>
                </div>
              </div>
            )}

            {/* ── SETTINGS ── */}
            {tab==='settings' && (
              <div style={{background:'#fff',border:'1px solid var(--border)',padding:28}}>
                <h2 style={{fontFamily:"'Cormorant Garamond',serif",fontSize:22,color:'#2d1f1f',marginBottom:24}}>Account Settings</h2>
                {[
                  {label:'Order Confirmation SMS',desc:'Receive SMS when your order is placed',defaultOn:true},
                  {label:'Shipping Updates',desc:'Get SMS when your order is shipped or out for delivery',defaultOn:true},
                  {label:'New Collection Alerts',desc:'Be the first to know about new saree collections',defaultOn:false},
                  {label:'Offer & Coupon Alerts',desc:'Receive exclusive offers and discount codes',defaultOn:true},
                ].map(setting => (
                  <SettingToggle key={setting.label} setting={setting}/>
                ))}

                <div style={{marginTop:28,paddingTop:20,borderTop:'1px solid var(--border)'}}>
                  <h3 style={{fontFamily:"'Jost',sans-serif",fontSize:12,letterSpacing:2,textTransform:'uppercase',color:'#c00',marginBottom:12}}>Danger Zone</h3>
                  <button onClick={()=>{if(window.confirm('Logout from this device?')){logout();navigate('/');}}} className="btn btn-outline" style={{borderColor:'#c00',color:'#c00',fontSize:11,padding:'8px 20px'}}>
                    Logout
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
